import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, addDoc, updateDoc, serverTimestamp, where, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaArrowLeft, 
  FaWeight, 
  FaUtensils, 
  FaFire, 
  FaTint, 
  FaCalendarAlt,
  FaUser,
  FaClipboardList,
  FaChevronLeft,
  FaChevronRight,
  FaStickyNote,
  FaAppleAlt,
  FaPlus,
  FaDumbbell,
  FaSave,
  FaTimes,
  FaClock,
  FaEdit,
  FaPause,
  FaPlay,
  FaVideo,
  FaCheck,
  FaShareAlt,
  FaYoutube,
  FaLock,
  FaLockOpen
} from 'react-icons/fa';
import styled from 'styled-components';

const Container = styled.div`
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const BackButton = styled.button`
  background: linear-gradient(135deg, #291759, #6c63ff);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  box-shadow: 0 4px 10px rgba(108, 99, 255, 0.3);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(108, 99, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ClientHeader = styled.div`
  flex: 1;
`;

const ClientName = styled.h1`
  color: #292929;
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
`;

const ClientEmail = styled.p`
  color: #666;
  margin: 0.25rem 0 0 0;
  font-size: 1.1rem;
`;

const TabContainer = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
  overflow: hidden;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const Tab = styled.button`
  flex: 1;
  background: ${props => props.active ? 'linear-gradient(135deg, #291759, #6c63ff)' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: ${props => props.active ? '600' : '500'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  position: relative;
  min-width: 150px;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #291759, #6c63ff)' : '#f8f9fa'};
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.active ? '#FFC107' : 'transparent'};
    transform: ${props => props.active ? 'scaleX(1)' : 'scaleX(0)'};
    transition: transform 0.3s ease;
  }
`;

const ContentArea = styled.div`
  margin-bottom: 2.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  padding: 1.75rem;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.03);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f1f3f4;
  padding-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const CardTitle = styled.h3`
  color: #292929;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.4rem;
  
  svg {
    color: #291759;
    font-size: 1.5rem;
  }
`;

const CardSubtitle = styled.p`
  color: #666;
  margin: 0.25rem 0 0 0;
  font-size: 0.95rem;
`;

const AddButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 1.5rem;
  text-align: center;
  border-left: 4px solid ${props => props.color || '#007bff'};
`;

const StatIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.color || '#007bff'};
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const DailyProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const DailyPlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 1.5rem;
`;

const MealSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MealHeader = styled.h4`
  color: #007bff;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MealDetail = styled.div`
  margin-bottom: 0.5rem;
  
  strong {
    color: #333;
    margin-right: 0.5rem;
  }
  
  span {
    color: #666;
  }
`;

const WorkoutSection = styled.div`
  background: #e8f5e8;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const WorkoutHeader = styled.h4`
  color: #28a745;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PlanCard = styled(Card)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  
  h3, h4, p {
    color: white;
  }
`;

const PlanDetail = styled.div`
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 700px;
  width: 95%;
  max-height: 85vh;
  overflow: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f1f3f4;
  padding-bottom: 1rem;
`;

const ModalTitle = styled.h2`
  color: #28a745;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  &:hover {
    background: #545b62;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  background: ${props => props.disabled ? '#ccc' : '#007bff'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.disabled ? '#ccc' : '#0056b3'};
  }
`;

const LoadingDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #666;
`;

const ErrorDiv = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SuccessDiv = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

// New styled components for videos
const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  }
`;

const VideoThumbnail = styled.div`
  height: 180px;
  background: #eee;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.2);
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: #f00;
    font-size: 24px;
  }
`;

const VideoInfo = styled.div`
  padding: 1.25rem;
`;

const VideoTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
`;

const VideoDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const VideoActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #eee;
  background: #f9fafb;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.shared ? '#4CAF50' : '#6c63ff'};
  color: white;
  border: none;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.shared ? '#43A047' : '#5753e6'};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ClientDetailsView = () => {
  const { clientId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [clientData, setClientData] = useState(null);
  const [healthProfile, setHealthProfile] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dailyProgress, setDailyProgress] = useState([]);
  const [dailyPlans, setDailyPlans] = useState([]);
  const [isPlanPaused, setIsPlanPaused] = useState(false);
  const [partnerVideos, setPartnerVideos] = useState([]);
  const [sharedVideos, setSharedVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [sharingVideo, setSharingVideo] = useState(false);

  // Add these to your existing state declarations
  const [editingCalorieGoal, setEditingCalorieGoal] = useState(false);
  const [calorieGoalValue, setCalorieGoalValue] = useState('');
  
  // Modal states
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [showWorkoutPlanModal, setShowWorkoutPlanModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [mealPlanForm, setMealPlanForm] = useState({
    date: new Date().toISOString().split('T')[0],
    breakfast: { time: '08:00', foods: '', calories: '', notes: '' },
    lunch: { time: '13:00', foods: '', calories: '', notes: '' },
    dinner: { time: '19:00', foods: '', calories: '', notes: '' },
    snacks: { foods: '', calories: '', notes: '' },
    totalCalories: '',
    waterGoal: '',
    notes: ''
  });
  
  const [workoutPlanForm, setWorkoutPlanForm] = useState({
    date: new Date().toISOString().split('T')[0],
    workoutType: 'strength', // strength, cardio, mixed, rest
    duration: '',
    exercises: '',
    sets: '',
    reps: '',
    weight: '',
    caloriesBurned: '',
    notes: '',
    restDay: false
  });
  
  // Pagination for daily progress
  const [currentPage, setCurrentPage] = useState(1);
  const [plansCurrentPage, setPlansCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  useEffect(() => {
    if (currentUser && clientId) {
      loadClientData();
    }
  }, [currentUser, clientId]);
  
  useEffect(() => {
    if (currentUser && clientId && activeTab === 'videos') {
      loadPartnerVideos();
    }
  }, [currentUser, clientId, activeTab]);
  
  const loadClientData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load client basic info
      const clientRef = doc(db, 'users', clientId);
      const clientDoc = await getDoc(clientRef);
      
      if (!clientDoc.exists()) {
        setError('Client not found');
        return;
      }
      
      const clientData = clientDoc.data();
      setClientData(clientData);
      
      // Check if the plan is paused
      setIsPlanPaused(clientData.isPlanPaused || false);
      
      // Load health profile
      try {
        const healthRef = doc(db, 'users', clientId, 'health', 'profile');
        const healthDoc = await getDoc(healthRef);
        if (healthDoc.exists()) {
          setHealthProfile(healthDoc.data());
        }
      } catch (err) {
        console.error('Error loading health profile:', err);
      }
      
      // Load selected plan
      try {
        const selectedPlanId = clientData.selectedPlanId;
        if (selectedPlanId) {
          const planRef = doc(db, 'plans', selectedPlanId);
          const planDoc = await getDoc(planRef);
          
          if (planDoc.exists()) {
            const planData = planDoc.data();
            const combinedPlan = {
              ...planData,
              planId: selectedPlanId,
              selectedAt: clientData.planSelectedAt || null
            };
            setSelectedPlan(combinedPlan);
          }
        }
      } catch (err) {
        console.error('Error loading selected plan:', err);
      }
      
      // Load daily progress
      try {
        const progressRef = collection(db, 'users', clientId, 'dailyProgress');
        const progressQuery = query(progressRef, orderBy('date', 'desc'), limit(30));
        const progressDocs = await getDocs(progressQuery);
        
        const progressData = [];
        progressDocs.forEach(doc => {
          progressData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setDailyProgress(progressData);
      } catch (err) {
        console.error('Error loading daily progress:', err);
      }
      
      // Load daily plans created by partner
      try {
        const plansRef = collection(db, 'users', clientId, 'dailyPlans');
        const plansQuery = query(plansRef, orderBy('date', 'desc'), limit(30));
        const plansDocs = await getDocs(plansQuery);
        
        const plansData = [];
        plansDocs.forEach(doc => {
          plansData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setDailyPlans(plansData);
      } catch (err) {
        console.error('Error loading daily plans:', err);
      }
      
      // If the active tab is videos, also load videos
      if (activeTab === 'videos') {
        loadPartnerVideos();
      }
      
    } catch (err) {
      console.error('Error loading client data:', err);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadPartnerVideos = async () => {
    try {
      setLoadingVideos(true);
      
      // Get all videos uploaded by this partner
      const videosRef = collection(db, 'videos');
      const videosQuery = query(
        videosRef, 
        where('partnerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const videosDocs = await getDocs(videosQuery);
      
      const videos = [];
      videosDocs.forEach(doc => {
        videos.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setPartnerVideos(videos);
      
      // Get videos shared with this client
      const sharedVideosRef = doc(db, 'users', clientId);
      const sharedVideosDoc = await getDoc(sharedVideosRef);
      
      if (sharedVideosDoc.exists()) {
        const userData = sharedVideosDoc.data();
        setSharedVideos(userData.sharedVideos || []);
      } else {
        setSharedVideos([]);
      }
      
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos');
    } finally {
      setLoadingVideos(false);
    }
  };
  
  const handleSaveMealPlan = async () => {
    try {
      setSaving(true);
      setError('');
      
      const planData = {
        type: 'meal',
        date: mealPlanForm.date,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        partnerName: currentUser.displayName || currentUser.email,
        breakfast: mealPlanForm.breakfast,
        lunch: mealPlanForm.lunch,
        dinner: mealPlanForm.dinner,
        snacks: mealPlanForm.snacks,
        totalCalories: parseInt(mealPlanForm.totalCalories) || 0,
        waterGoal: mealPlanForm.waterGoal,
        notes: mealPlanForm.notes
      };
      
      const plansRef = collection(db, 'users', clientId, 'dailyPlans');
      await addDoc(plansRef, planData);
      
      setSuccess('Meal plan saved successfully!');
      setShowMealPlanModal(false);
      
      // Reset form
      setMealPlanForm({
        date: new Date().toISOString().split('T')[0],
        breakfast: { time: '08:00', foods: '', calories: '', notes: '' },
        lunch: { time: '13:00', foods: '', calories: '', notes: '' },
        dinner: { time: '19:00', foods: '', calories: '', notes: '' },
        snacks: { foods: '', calories: '', notes: '' },
        totalCalories: '',
        waterGoal: '',
        notes: ''
      });
      
      // Reload daily plans
      loadClientData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving meal plan:', err);
      setError('Failed to save meal plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCalorieGoal = async () => {
    try {
      setSaving(true);
      setError('');
      
      const healthRef = doc(db, 'users', clientId, 'health', 'profile');
      await updateDoc(healthRef, {
        dailyCalorieGoal: parseInt(calorieGoalValue) || 0,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      
      // Update local state
      setHealthProfile(prev => ({
        ...prev,
        dailyCalorieGoal: parseInt(calorieGoalValue) || 0
      }));
      
      setEditingCalorieGoal(false);
      setSuccess('Daily calorie goal updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error updating calorie goal:', err);
      setError('Failed to update calorie goal');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveWorkoutPlan = async () => {
    try {
      setSaving(true);
      setError('');
      
      const planData = {
        type: 'workout',
        date: workoutPlanForm.date,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        partnerName: currentUser.displayName || currentUser.email,
        workoutType: workoutPlanForm.workoutType,
        duration: parseInt(workoutPlanForm.duration) || 0,
        exercises: workoutPlanForm.exercises,
        sets: workoutPlanForm.sets,
        reps: workoutPlanForm.reps,
        weight: workoutPlanForm.weight,
        caloriesBurned: parseInt(workoutPlanForm.caloriesBurned) || 0,
        notes: workoutPlanForm.notes,
        restDay: workoutPlanForm.restDay
      };
      
      const plansRef = collection(db, 'users', clientId, 'dailyPlans');
      await addDoc(plansRef, planData);
      
      setSuccess('Workout plan saved successfully!');
      setShowWorkoutPlanModal(false);
      
      // Reset form
      setWorkoutPlanForm({
        date: new Date().toISOString().split('T')[0],
        workoutType: 'strength',
        duration: '',
        exercises: '',
        sets: '',
        reps: '',
        weight: '',
        caloriesBurned: '',
        notes: '',
        restDay: false
      });
      
      // Reload daily plans
      loadClientData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving workout plan:', err);
      setError('Failed to save workout plan');
    } finally {
      setSaving(false);
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const formatHealthGoal = (goal) => {
    const goals = {
      'weight-loss': 'Weight Loss',
      'muscle-gain': 'Muscle Gain',
      'maintenance': 'Weight Maintenance',
      'health-improvement': 'General Health',
      'athletic-performance': 'Athletic Performance'
    };
    return goals[goal] || goal;
  };
  
  const formatActivityLevel = (level) => {
    const levels = {
      'sedentary': 'Sedentary',
      'light': 'Lightly Active',
      'moderate': 'Moderately Active',
      'very': 'Very Active',
      'extra': 'Extra Active'
    };
    return levels[level] || level;
  };
  
  // Calculate the plan end date including extension from paused days
  const calculatePlanEndDate = () => {
    if (!selectedPlan || !clientData?.planSelectedAt) return 'N/A';
    
    const planStartDate = clientData.planSelectedAt.toDate ? 
      clientData.planSelectedAt.toDate() : new Date(clientData.planSelectedAt);
      
    if (!planStartDate) return 'N/A';
    
    const totalDays = parseInt(selectedPlan.duration) || 30;
    const pausedDays = clientData?.planPausedDays || 0;
    
    // Calculate end date with extension for paused days
    const endDate = new Date(planStartDate);
    endDate.setDate(planStartDate.getDate() + totalDays + pausedDays);
    
    return endDate.toLocaleDateString();
  };
  
  // Pagination logic
  const totalPages = Math.ceil(dailyProgress.length / itemsPerPage);
  const paginatedProgress = dailyProgress.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const plansTotalPages = Math.ceil(dailyPlans.length / itemsPerPage);
  const paginatedPlans = dailyPlans.slice(
    (plansCurrentPage - 1) * itemsPerPage,
    plansCurrentPage * itemsPerPage
  );
  
  // Add new function to handle pausing/resuming a client's plan
  const handleTogglePlanStatus = async (pauseAction) => {
    try {
      setSaving(true);
      setError('');
      
      const clientRef = doc(db, 'users', clientId);
      
      if (pauseAction) {
        // Pause the plan
        await updateDoc(clientRef, {
          isPlanPaused: true,
          planPausedAt: serverTimestamp()
        });
        setIsPlanPaused(true);
        setSuccess('Plan has been paused successfully. The end date will be extended when resumed.');
      } else {
        // Resume the plan
        // Calculate additional paused days to extend the plan end date
        let additionalPausedDays = 0;
        
        if (clientData.planPausedAt) {
          const pauseDate = clientData.planPausedAt.toDate ? 
            clientData.planPausedAt.toDate() : new Date(clientData.planPausedAt);
          const now = new Date();
          additionalPausedDays = Math.floor((now - pauseDate) / (1000 * 60 * 60 * 24));
        }
        
        await updateDoc(clientRef, {
          isPlanPaused: false,
          planPausedAt: null,
          planPausedDays: (clientData.planPausedDays || 0) + additionalPausedDays
        });
        setIsPlanPaused(false);
        setSuccess(`Plan has been resumed successfully. End date extended by ${additionalPausedDays} day${additionalPausedDays !== 1 ? 's' : ''}.`);
      }
      
      // Update the local client data
      const updatedClientDoc = await getDoc(clientRef);
      if (updatedClientDoc.exists()) {
        setClientData(updatedClientDoc.data());
      }
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      
    } catch (err) {
      console.error('Error updating plan status:', err);
      setError('Failed to update plan status');
    } finally {
      setSaving(false);
    }
  };
  
  const handleShareVideo = async (videoId) => {
    try {
      setSharingVideo(true);
      setError('');
      
      const isAlreadyShared = sharedVideos.includes(videoId);
      const clientRef = doc(db, 'users', clientId);
      
      if (isAlreadyShared) {
        // Remove video from shared list
        await updateDoc(clientRef, {
          sharedVideos: arrayRemove(videoId)
        });
        
        setSharedVideos(prev => prev.filter(id => id !== videoId));
        setSuccess('Video removed from client');
      } else {
        // Add video to shared list
        await updateDoc(clientRef, {
          sharedVideos: arrayUnion(videoId)
        });
        
        setSharedVideos(prev => [...prev, videoId]);
        setSuccess('Video shared with client');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error sharing video:', err);
      setError('Failed to update video sharing');
    } finally {
      setSharingVideo(false);
    }
  };
  
  if (loading) {
    return (
      <Container>
        <LoadingDiv>Loading client details...</LoadingDiv>
      </Container>
    );
  }
  
  if (error && !success) {
    return (
      <Container>
        <ErrorDiv>{error}</ErrorDiv>
        <BackButton onClick={() => navigate('/partner/clients')}>
          <FaArrowLeft /> Back to Clients
        </BackButton>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/partner/clients')}>
          <FaArrowLeft /> Back to Clients
        </BackButton>
        <ClientHeader>
          <ClientName>{clientData?.displayName || 'Client'}</ClientName>
          <ClientEmail>{clientData?.email}</ClientEmail>
        </ClientHeader>
      </Header>
      
      {success && (
        <SuccessDiv>{success}</SuccessDiv>
      )}
      
      {error && (
        <ErrorDiv>{error}</ErrorDiv>
      )}
      
      <TabContainer>
        <Tab 
          active={activeTab === 'overview'} 
          onClick={() => setActiveTab('overview')}
        >
          <FaUser /> Overview
        </Tab>
        <Tab 
          active={activeTab === 'progress'} 
          onClick={() => setActiveTab('progress')}
        >
          <FaCalendarAlt /> Daily Progress
        </Tab>
        <Tab 
          active={activeTab === 'plan'} 
          onClick={() => setActiveTab('plan')}
        >
          <FaClipboardList /> Selected Plan
        </Tab>
        <Tab 
          active={activeTab === 'daily-plans'} 
          onClick={() => setActiveTab('daily-plans')}
        >
          <FaUtensils /> Daily Plans
        </Tab>
        <Tab 
          active={activeTab === 'videos'} 
          onClick={() => setActiveTab('videos')}
        >
          <FaVideo /> Videos
        </Tab>
      </TabContainer>
      
      <ContentArea>
        {activeTab === 'overview' && (
          <>
            <StatsGrid>
              <StatCard color="#007bff">
                <StatIcon color="#007bff"><FaWeight /></StatIcon>
                <StatValue>{healthProfile?.weight || 'N/A'} kg</StatValue>
                <StatLabel>Current Weight</StatLabel>
              </StatCard>
              
              <StatCard color="#28a745">
                <StatIcon color="#28a745"></StatIcon>
                <StatValue>{healthProfile?.targetWeight || 'N/A'} kg</StatValue>
                <StatLabel>Target Weight</StatLabel>
              </StatCard>
              
              <StatCard color="#17a2b8">
                <StatIcon color="#17a2b8"><FaFire /></StatIcon>
                {editingCalorieGoal ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="number"
                      value={calorieGoalValue}
                      onChange={(e) => setCalorieGoalValue(e.target.value)}
                      style={{
                        width: '80px',
                        padding: '4px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                      placeholder="Goal"
                    />
                    <FaSave 
                      style={{ cursor: 'pointer', color: '#28a745' }}
                      onClick={handleSaveCalorieGoal}
                    />
                    <FaTimes 
                      style={{ cursor: 'pointer', color: '#dc3545' }}
                      onClick={() => {
                        setEditingCalorieGoal(false);
                        setCalorieGoalValue(healthProfile?.dailyCalorieGoal || '');
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <StatValue>{healthProfile?.dailyCalorieGoal || 'N/A'}</StatValue>
                    <FaEdit 
                      style={{ cursor: 'pointer', fontSize: '14px', color: '#6c757d' }}
                      onClick={() => {
                        setEditingCalorieGoal(true);
                        setCalorieGoalValue(healthProfile?.dailyCalorieGoal || '');
                      }}
                    />
                  </div>
                )}
                <StatLabel>Daily Calorie Goal</StatLabel>
              </StatCard>
              
              <StatCard color="#ffc107">
                <StatIcon color="#ffc107"><FaCalendarAlt /></StatIcon>
                <StatValue>{dailyProgress.length}</StatValue>
                <StatLabel>Days Tracked</StatLabel>
              </StatCard>
            </StatsGrid>
            
            {healthProfile && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <FaUser />
                    Health Profile
                  </CardTitle>
                </CardHeader>
                <StatsGrid>
                  <div>
                    <strong>Age:</strong> {healthProfile.age || 'N/A'} years
                  </div>
                  <div>
                    <strong>Gender:</strong> {healthProfile.gender || 'N/A'}
                  </div>
                  <div>
                    <strong>Height:</strong> {healthProfile.height || 'N/A'} cm
                  </div>
                  <div>
                    <strong>Health Goal:</strong> {formatHealthGoal(healthProfile.healthGoals)}
                  </div>
                  <div>
                    <strong>Activity Level:</strong> {formatActivityLevel(healthProfile.activityLevel)}
                  </div>
                  <div>
                    <strong>Dietary Restrictions:</strong> {healthProfile.dietaryRestrictions || 'None'}
                  </div>
                </StatsGrid>
                {healthProfile.medicalConditions && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Medical Conditions:</strong> {healthProfile.medicalConditions}
                  </div>
                )}
              </Card>
            )}
          </>
        )}
        
        {activeTab === 'progress' && (
          <>
            {dailyProgress.length === 0 ? (
              <EmptyState>
                <h3>No Progress Data</h3>
                <p>This client hasn't logged any daily progress yet.</p>
              </EmptyState>
            ) : (
              <>
                <DailyProgressGrid>
                  {paginatedProgress.map(progress => (
                    <Card key={progress.id}>
                      <CardHeader>
                        <div>
                          <CardTitle>
                            <FaCalendarAlt />
                            {formatDate(progress.date)}
                          </CardTitle>
                          <CardSubtitle>
                            Weight: {progress.weight ? `${progress.weight} kg` : 'Not recorded'}
                          </CardSubtitle>
                        </div>
                      </CardHeader>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ color: '#28a745' }}>
                            <FaFire style={{ marginRight: '0.5rem' }} />
                            Total Calories: {progress.totalCalories || 0}
                          </strong>
                          <span style={{ color: '#17a2b8' }}>
                            <FaTint style={{ marginRight: '0.5rem' }} />
                            Water: {progress.waterIntake || 'N/A'}
                          </span>
                        </div>
                      </div>
                      
                      {progress.meals && (
                        <>
                          {['breakfast', 'lunch', 'dinner'].map(mealType => {
                            const meal = progress.meals[mealType];
                            if (!meal || (!meal.foods && !meal.calories)) return null;
                            
                            return (
                              <MealSection key={mealType}>
                                <MealHeader>
                                  <FaAppleAlt />
                                  {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                                </MealHeader>
                                {meal.foods && (
                                  <MealDetail>
                                    <strong>Foods:</strong>
                                    <span>{meal.foods}</span>
                                  </MealDetail>
                                )}
                                {meal.calories && (
                                  <MealDetail>
                                    <strong>Calories:</strong>
                                    <span>{meal.calories}</span>
                                  </MealDetail>
                                )}
                                {meal.notes && (
                                  <MealDetail>
                                    <strong>Notes:</strong>
                                    <span>{meal.notes}</span>
                                  </MealDetail>
                                )}
                              </MealSection>
                            );
                          })}
                        </>
                      )}
                      
                      {progress.notes && (
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '0.75rem', 
                          background: '#f8f9fa', 
                          borderRadius: '6px',
                          borderLeft: '3px solid #ffc107'
                        }}>
                          <strong style={{ color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FaStickyNote />
                            Daily Notes:
                          </strong>
                          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                            {progress.notes}
                          </p>
                        </div>
                      )}
                    </Card>
                  ))}
                </DailyProgressGrid>
                
                {totalPages > 1 && (
                  <Pagination>
                    <PageButton 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      <FaChevronLeft /> Previous
                    </PageButton>
                    
                    <span style={{ color: '#666' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    
                    <PageButton 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next <FaChevronRight />
                    </PageButton>
                  </Pagination>
                )}
              </>
            )}
          </>
        )}
        
        {activeTab === 'plan' && (
          <>
            {selectedPlan ? (
              <PlanCard>
                <CardHeader>
                  <CardTitle>
                    <FaClipboardList />
                    {selectedPlan.name || selectedPlan.title || 'Selected Plan'}
                  </CardTitle>
                  
                  {/* Add plan status indicator and pause/resume buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {isPlanPaused ? (
                      <div style={{
                        background: '#fef3c7',
                        color: '#d97706',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FaPause /> Plan Paused
                      </div>
                    ) : (
                      <div style={{
                        background: '#dcfce7',
                        color: '#16a34a',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FaPlay /> Plan Active
                      </div>
                    )}
                    
                    {isPlanPaused ? (
                      <AddButton 
                        onClick={() => handleTogglePlanStatus(false)} 
                        disabled={saving}
                        style={{
                          background: '#16a34a',
                          color: 'white'
                        }}
                      >
                        <FaPlay /> Resume Plan
                      </AddButton>
                    ) : (
                      <AddButton 
                        onClick={() => handleTogglePlanStatus(true)} 
                        disabled={saving}
                        style={{
                          background: '#d97706',
                          color: 'white'
                        }}
                      >
                        <FaPause /> Pause Plan
                      </AddButton>
                    )}
                  </div>
                </CardHeader>
                
                <PlanDetail>
                  <h4>Plan Details</h4>
                  <p><strong>Duration:</strong> {selectedPlan.duration || 'N/A'} days</p>
                  <p><strong>Goal:</strong> {selectedPlan.goal || selectedPlan.targetGoal || 'N/A'}</p>
                  {selectedPlan.selectedAt && (
                    <p><strong>Selected on:</strong> {
                      selectedPlan.selectedAt.toDate ? 
                        new Date(selectedPlan.selectedAt.toDate()).toLocaleDateString() : 
                        typeof selectedPlan.selectedAt === 'string' ? 
                          new Date(selectedPlan.selectedAt).toLocaleDateString() : 
                          'N/A'
                    }</p>
                  )}
                  <p><strong>End date:</strong> {calculatePlanEndDate()}</p>
                  {clientData && clientData.planPausedDays > 0 && (
                    <p><strong>Total Days Paused:</strong> {clientData.planPausedDays} (extends end date)</p>
                  )}
                </PlanDetail>
                
                {selectedPlan.description && (
                  <PlanDetail>
                    <h4>Description</h4>
                    <p>{selectedPlan.description}</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.dietPlan && (
                  <PlanDetail>
                    <h4>Diet Plan</h4>
                    <p>{selectedPlan.dietPlan}</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.workoutPlan && (
                  <PlanDetail>
                    <h4>Workout Plan</h4>
                    <p>{selectedPlan.workoutPlan}</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.targetCalories && (
                  <PlanDetail>
                    <h4>Target Calories</h4>
                    <p>{selectedPlan.targetCalories} calories per day</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.instructions && (
                  <PlanDetail>
                    <h4>Instructions</h4>
                    <p>{selectedPlan.instructions}</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.meals && (
                  <PlanDetail>
                    <h4>Meal Plan</h4>
                    <p>{typeof selectedPlan.meals === 'string' ? selectedPlan.meals : JSON.stringify(selectedPlan.meals, null, 2)}</p>
                  </PlanDetail>
                )}
                
                {selectedPlan.exercises && (
                  <PlanDetail>
                    <h4>Exercise Plan</h4>
                    <p>{typeof selectedPlan.exercises === 'string' ? selectedPlan.exercises : JSON.stringify(selectedPlan.exercises, null, 2)}</p>
                  </PlanDetail>
                )}
              </PlanCard>
            ) : (
              <EmptyState>
                <h3>No Plan Selected</h3>
                <p>This client hasn't selected a plan yet.</p>
              </EmptyState>
            )}
          </>
        )}
        
        {activeTab === 'daily-plans' && (
          <>
            <Card style={{
              background: 'linear-gradient(135deg, #291759, #6c63ff)',
              color: 'white'
            }}>
              <CardHeader style={{ borderBottom: '2px solid rgba(255,255,255,0.2)' }}>
                <CardTitle style={{ color: 'white' }}>
                  <FaClipboardList />
                  Daily Plans Management
                </CardTitle>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <AddButton onClick={() => setShowMealPlanModal(true)} style={{
                    background: '#28a745',
                    boxShadow: '0 4px 10px rgba(40, 167, 69, 0.3)',
                    padding: '0.75rem 1.25rem',
                    borderRadius: '12px',
                    fontWeight: '600'
                  }}>
                    <FaPlus /> Add Meal Plan
                  </AddButton>
                  <AddButton onClick={() => setShowWorkoutPlanModal(true)} style={{
                    background: '#fd7e14',
                    boxShadow: '0 4px 10px rgba(253, 126, 20, 0.3)',
                    padding: '0.75rem 1.25rem',
                    borderRadius: '12px',
                    fontWeight: '600'
                  }}>
                    <FaDumbbell /> Add Workout Plan
                  </AddButton>
                </div>
              </CardHeader>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0' }}>
                Create personalized meal and workout plans for your client. These plans will be immediately visible to them in their daily plans section.
              </p>
            </Card>
            
            {dailyPlans.length === 0 ? (
              <EmptyState style={{
                background: 'white',
                borderRadius: '16px',
                padding: '4rem 2rem',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <div style={{ fontSize: '5rem', color: '#ddd', marginBottom: '1rem' }}>
                  <FaClipboardList />
                </div>
                <h3 style={{ fontSize: '1.5rem', margin: '0 0 0.75rem 0', color: '#333' }}>
                  No Daily Plans Created Yet
                </h3>
                <p style={{ fontSize: '1.1rem', color: '#666', maxWidth: '500px', margin: '0 auto' }}>
                  Create meal plans and workout plans for your client using the buttons above. 
                  These will help guide their nutrition and exercise routines.
                </p>
              </EmptyState>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0', color: '#333', fontSize: '1.3rem' }}>
                    Created Plans ({dailyPlans.length})
                  </h3>
                </div>
                
                <DailyPlansGrid style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {paginatedPlans.map(plan => (
                    <Card key={plan.id} style={{
                      border: `1px solid ${plan.type === 'meal' ? '#28a745' : '#fd7e14'}20`,
                      position: 'relative',
                      overflow: 'hidden',
                      padding: '0',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        height: '8px',
                        background: plan.type === 'meal' 
                          ? 'linear-gradient(90deg, #28a745, #5cb85c)' 
                          : 'linear-gradient(90deg, #fd7e14, #f0ad4e)',
                        width: '100%'
                      }} />
                      
                      <CardHeader style={{
                        background: plan.type === 'meal' 
                          ? 'rgba(40, 167, 69, 0.05)' 
                          : 'rgba(253, 126, 20, 0.05)',
                        borderRadius: '0',
                        margin: '0',
                        padding: '1.25rem'
                      }}>
                        <div>
                          <CardTitle style={{ fontSize: '1.3rem', color: plan.type === 'meal' ? '#28a745' : '#fd7e14' }}>
                            {plan.type === 'meal' ? <FaUtensils /> : <FaDumbbell />}
                            {formatDate(plan.date)}
                          </CardTitle>
                          <CardSubtitle>
                            {plan.type === 'meal' ? 'Meal Plan' : 'Workout Plan'} by {plan.partnerName}
                          </CardSubtitle>
                        </div>
                        
                        <div style={{
                          background: plan.type === 'meal' ? '#28a74520' : '#fd7e1420',
                          color: plan.type === 'meal' ? '#28a745' : '#fd7e14',
                          padding: '0.5rem 1rem',
                          borderRadius: '2rem',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {plan.type === 'meal' ? 'MEAL' : 'WORKOUT'}
                        </div>
                      </CardHeader>
                      
                      <div style={{ padding: '1.25rem', flex: '1' }}>
                        {plan.type === 'meal' && (
                          <>
                            <MealSection style={{
                              borderRadius: '12px',
                              background: '#f9fff9',
                              border: '1px solid #e0f0e0',
                              padding: '1.25rem',
                              marginBottom: '1rem'
                            }}>
                              <MealHeader style={{
                                color: '#28a745',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '0.75rem',
                                fontWeight: '600'
                              }}>
                                <FaClock />
                                Breakfast - {plan.breakfast?.time || '08:00'}
                              </MealHeader>
                              
                              {plan.breakfast?.foods && (
                                <MealDetail style={{ marginBottom: '0.5rem', lineHeight: '1.5' }}>
                                  <strong style={{ color: '#333', marginRight: '0.5rem' }}>Foods:</strong>
                                  <span style={{ color: '#555' }}>{plan.breakfast.foods}</span>
                                </MealDetail>
                              )}
                              
                              {plan.breakfast?.calories && (
                                <div style={{
                                  display: 'inline-block',
                                  background: '#28a74510',
                                  color: '#28a745',
                                  padding: '0.35rem 0.75rem',
                                  borderRadius: '2rem',
                                  fontSize: '0.9rem',
                                  marginTop: '0.5rem'
                                }}>
                                  {plan.breakfast.calories} calories
                                </div>
                              )}
                            </MealSection>
                            
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginTop: '1rem',
                              padding: '0.75rem',
                              background: '#e8f5e8',
                              borderRadius: '12px'
                            }}>
                              <strong style={{ color: '#28a745', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FaFire />
                                Total: {plan.totalCalories || 0} cal
                              </strong>
                              {plan.waterGoal && (
                                <span style={{ color: '#17a2b8', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <FaTint />
                                  Water: {plan.waterGoal}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        
                        {plan.type === 'workout' && (
                          <>
                            {plan.restDay ? (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '2rem',
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                color: '#666'
                              }}>
                                <h4 style={{ color: '#fd7e14', margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>Rest Day</h4>
                                <p style={{ margin: '0', color: '#666' }}>Take a well-deserved break today!</p>
                              </div>
                            ) : (
                              <>
                                <WorkoutSection style={{
                                  borderRadius: '12px',
                                  background: '#fff9f2',
                                  border: '1px solid #f0e0d0',
                                  padding: '1.25rem',
                                  marginBottom: '1rem'
                                }}>
                                  <WorkoutHeader style={{
                                    color: '#fd7e14',
                                    fontSize: '1.1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem',
                                    fontWeight: '600'
                                  }}>
                                    <FaDumbbell />
                                    {plan.workoutType?.charAt(0).toUpperCase() + plan.workoutType?.slice(1) || 'Workout'}
                                  </WorkoutHeader>
                                  
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.75rem',
                                    marginBottom: '1rem'
                                  }}>
                                    {plan.duration && (
                                      <div style={{
                                        background: '#fd7e1410',
                                        color: '#fd7e14',
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '2rem',
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}>
                                        <FaClock /> {plan.duration} min
                                      </div>
                                    )}
                                    
                                    {plan.caloriesBurned && (
                                      <div style={{
                                        background: '#fd7e1410',
                                        color: '#fd7e14',
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '2rem',
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}>
                                        <FaFire /> {plan.caloriesBurned} cal
                                      </div>
                                    )}
                                  </div>
                                  
                                  {plan.exercises && (
                                    <MealDetail style={{ marginBottom: '0.5rem', lineHeight: '1.5' }}>
                                      <strong style={{ color: '#333', marginRight: '0.5rem', display: 'block', marginBottom: '0.5rem' }}>
                                        Exercises:
                                      </strong>
                                      <span style={{ color: '#555', whiteSpace: 'pre-line' }}>{plan.exercises}</span>
                                    </MealDetail>
                                  )}
                                </WorkoutSection>
                              </>
                            )}
                          </>
                        )}
                        
                        {plan.notes && (
                          <div style={{ 
                            marginTop: '1rem', 
                            padding: '0.75rem', 
                            background: '#f9fafb', 
                            borderRadius: '10px',
                            borderLeft: '3px solid #ffc107'
                          }}>
                            <strong style={{ color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <FaStickyNote />
                              Notes:
                            </strong>
                            <p style={{ margin: '0.5rem 0 0 0', color: '#666', lineHeight: '1.5' }}>
                              {plan.notes}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        borderTop: '1px solid #eee',
                        padding: '1rem 1.25rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: '#f9fafb'
                      }}>
                        <span style={{ color: '#666', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FaCalendarAlt /> 
                          {new Date(plan.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span style={{ color: '#666', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FaUser />
                          Created by you
                        </span>
                      </div>
                    </Card>
                  ))}
                </DailyPlansGrid>
                
                {plansTotalPages > 1 && (
                  <Pagination>
                    <PageButton 
                      disabled={plansCurrentPage === 1}
                      onClick={() => setPlansCurrentPage(prev => prev - 1)}
                    >
                      <FaChevronLeft /> Previous
                    </PageButton>
                    
                    <span style={{ color: '#666' }}>
                      Page {plansCurrentPage} of {plansTotalPages}
                    </span>
                    
                    <PageButton 
                      disabled={plansCurrentPage === plansTotalPages}
                      onClick={() => setPlansCurrentPage(prev => prev + 1)}
                    >
                      Next <FaChevronRight />
                    </PageButton>
                  </Pagination>
                )}
              </>
            )}
          </>
        )}
        
        {activeTab === 'videos' && (
          <>
            <Card style={{
              background: 'linear-gradient(135deg, #291759, #6c63ff)',
              color: 'white'
            }}>
              <CardHeader style={{ borderBottom: '2px solid rgba(255,255,255,0.2)' }}>
                <CardTitle style={{ color: 'white' }}>
                  <FaVideo />
                  Video Sharing
                </CardTitle>
                <div>
                  <AddButton onClick={() => navigate('/partner/videos')} style={{
                    background: '#28a745',
                    boxShadow: '0 4px 10px rgba(40, 167, 69, 0.3)',
                    padding: '0.75rem 1.25rem',
                    borderRadius: '12px',
                    fontWeight: '600'
                  }}>
                    <FaPlus /> Manage Videos
                  </AddButton>
                </div>
              </CardHeader>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0' }}>
                Share instructional and educational videos with your client. These videos will be immediately visible to them in their dashboard.
              </p>
            </Card>
            
            {loadingVideos ? (
              <LoadingDiv>Loading videos...</LoadingDiv>
            ) : partnerVideos.length === 0 ? (
              <EmptyState style={{
                background: 'white',
                borderRadius: '16px',
                padding: '4rem 2rem',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <div style={{ fontSize: '5rem', color: '#ddd', marginBottom: '1rem' }}>
                  <FaVideo />
                </div>
                <h3 style={{ fontSize: '1.5rem', margin: '0 0 0.75rem 0', color: '#333' }}>
                  No Videos Available
                </h3>
                <p style={{ fontSize: '1.1rem', color: '#666', maxWidth: '500px', margin: '0 auto' }}>
                  You haven't uploaded any videos yet. Go to the Video Management page to upload videos that you can share with your clients.
                </p>
                <AddButton onClick={() => navigate('/partner/videos')} style={{
                  background: '#28a745',
                  margin: '1.5rem auto 0',
                  display: 'inline-flex'
                }}>
                  <FaPlus /> Upload Videos
                </AddButton>
              </EmptyState>
            ) : (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0', color: '#333', fontSize: '1.3rem' }}>
                    Your Videos ({partnerVideos.length})
                  </h3>
                  <p style={{ color: '#666', margin: '0.5rem 0 0 0' }}>
                    Videos you can share with {clientData?.displayName || 'this client'}
                  </p>
                </div>
                
                <VideoGrid>
                  {partnerVideos.map(video => {
                    const isShared = sharedVideos.includes(video.id);
                    return (
                      <VideoCard key={video.id}>
                        <VideoThumbnail>
                          <img 
                            src={video.thumbnailUrl || 'https://placehold.co/300x180?text=Video+Thumbnail'} 
                            alt={video.title} 
                          />
                          <PlayButton>
                            <FaYoutube />
                          </PlayButton>
                          {isShared && (
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              background: 'rgba(40, 167, 69, 0.9)',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <FaLockOpen /> Shared
                            </div>
                          )}
                          {!isShared && (
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              background: 'rgba(108, 117, 125, 0.9)',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <FaLock /> Not Shared
                            </div>
                          )}
                        </VideoThumbnail>
                        <VideoInfo>
                          <VideoTitle>{video.title}</VideoTitle>
                          <VideoDescription>
                            {video.description && video.description.length > 100
                              ? `${video.description.substring(0, 100)}...`
                              : video.description || 'No description available.'}
                          </VideoDescription>
                        </VideoInfo>
                        <VideoActions>
                          <span style={{ color: '#666', fontSize: '0.9rem' }}>
                            {new Date(video.createdAt?.toDate?.() || video.createdAt).toLocaleDateString()}
                          </span>
                          <ShareButton 
                            shared={isShared} 
                            onClick={() => handleShareVideo(video.id)}
                            disabled={sharingVideo}
                          >
                            {isShared ? (
                              <>
                                <FaTimes /> Unshare
                              </>
                            ) : (
                              <>
                                <FaShareAlt /> Share
                              </>
                            )}
                          </ShareButton>
                        </VideoActions>
                      </VideoCard>
                    );
                  })}
                </VideoGrid>
              </>
            )}
          </>
        )}
      </ContentArea>
      
      {/* Meal Plan Modal */}
      {showMealPlanModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaUtensils />
                Create Daily Meal Plan
              </ModalTitle>
              <CloseButton onClick={() => setShowMealPlanModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                value={mealPlanForm.date}
                onChange={(e) => setMealPlanForm(prev => ({ ...prev, date: e.target.value }))}
              />
            </FormGroup>
            
            {/* Breakfast */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#007bff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaClock /> Breakfast
              </h4>
              <FormGroup>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={mealPlanForm.breakfast.time}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    breakfast: { ...prev.breakfast, time: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Foods</Label>
                <TextArea
                  placeholder="List the foods for breakfast..."
                  value={mealPlanForm.breakfast.foods}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    breakfast: { ...prev.breakfast, foods: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Calories</Label>
                <Input
                  type="number"
                  placeholder="Estimated calories"
                  value={mealPlanForm.breakfast.calories}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    breakfast: { ...prev.breakfast, calories: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Notes</Label>
                <TextArea
                  placeholder="Any special instructions or notes..."
                  value={mealPlanForm.breakfast.notes}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    breakfast: { ...prev.breakfast, notes: e.target.value }
                  }))}
                />
              </FormGroup>
            </div>
            
            {/* Lunch */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#007bff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaClock /> Lunch
              </h4>
              <FormGroup>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={mealPlanForm.lunch.time}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    lunch: { ...prev.lunch, time: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Foods</Label>
                <TextArea
                  placeholder="List the foods for lunch..."
                  value={mealPlanForm.lunch.foods}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    lunch: { ...prev.lunch, foods: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Calories</Label>
                <Input
                  type="number"
                  placeholder="Estimated calories"
                  value={mealPlanForm.lunch.calories}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    lunch: { ...prev.lunch, calories: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Notes</Label>
                <TextArea
                  placeholder="Any special instructions or notes..."
                  value={mealPlanForm.lunch.notes}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    lunch: { ...prev.lunch, notes: e.target.value }
                  }))}
                />
              </FormGroup>
            </div>
            
            {/* Dinner */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#007bff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaClock /> Dinner
              </h4>
              <FormGroup>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={mealPlanForm.dinner.time}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    dinner: { ...prev.dinner, time: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Foods</Label>
                <TextArea
                  placeholder="List the foods for dinner..."
                  value={mealPlanForm.dinner.foods}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    dinner: { ...prev.dinner, foods: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Calories</Label>
                <Input
                  type="number"
                  placeholder="Estimated calories"
                  value={mealPlanForm.dinner.calories}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    dinner: { ...prev.dinner, calories: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Notes</Label>
                <TextArea
                  placeholder="Any special instructions or notes..."
                  value={mealPlanForm.dinner.notes}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    dinner: { ...prev.dinner, notes: e.target.value }
                  }))}
                />
              </FormGroup>
            </div>
            
            {/* Snacks */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#007bff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaAppleAlt /> Snacks
              </h4>
              <FormGroup>
                <Label>Foods</Label>
                <TextArea
                  placeholder="List snacks (optional)..."
                  value={mealPlanForm.snacks.foods}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    snacks: { ...prev.snacks, foods: e.target.value }
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Calories</Label>
                <Input
                  type="number"
                  placeholder="Estimated calories"
                  value={mealPlanForm.snacks.calories}
                  onChange={(e) => setMealPlanForm(prev => ({ 
                    ...prev, 
                    snacks: { ...prev.snacks, calories: e.target.value }
                  }))}
                />
              </FormGroup>
            </div>
            
            <FormGroup>
              <Label>Total Daily Calories</Label>
              <Input
                type="number"
                placeholder="Total calories for the day"
                value={mealPlanForm.totalCalories}
                onChange={(e) => setMealPlanForm(prev => ({ ...prev, totalCalories: e.target.value }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Water Goal</Label>
              <Input
                type="text"
                placeholder="e.g., 8 glasses, 2 liters"
                value={mealPlanForm.waterGoal}
                onChange={(e) => setMealPlanForm(prev => ({ ...prev, waterGoal: e.target.value }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>General Notes</Label>
              <TextArea
                placeholder="Any additional notes or instructions for the day..."
                value={mealPlanForm.notes}
                onChange={(e) => setMealPlanForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </FormGroup>
            
            <ButtonGroup>
              <CancelButton onClick={() => setShowMealPlanModal(false)}>
                <FaTimes /> Cancel
              </CancelButton>
              <SaveButton onClick={handleSaveMealPlan} disabled={saving}>
                <FaSave /> {saving ? 'Saving...' : 'Save Meal Plan'}
              </SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      
      {/* Workout Plan Modal */}
      {showWorkoutPlanModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaDumbbell />
                Create Daily Workout Plan
              </ModalTitle>
              <CloseButton onClick={() => setShowWorkoutPlanModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                value={workoutPlanForm.date}
                onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, date: e.target.value }))}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={workoutPlanForm.restDay}
                  onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, restDay: e.target.checked }))}
                  style={{ marginRight: '0.5rem' }}
                />
                Rest Day
              </Label>
            </FormGroup>
            
            {!workoutPlanForm.restDay && (
              <>
                <FormGroup>
                  <Label>Workout Type</Label>
                  <Select
                    value={workoutPlanForm.workoutType}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, workoutType: e.target.value }))}
                  >
                    <option value="strength">Strength Training</option>
                    <option value="cardio">Cardio</option>
                    <option value="mixed">Mixed Training</option>
                    <option value="yoga">Yoga/Stretching</option>
                    <option value="sports">Sports Activity</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 45"
                    value={workoutPlanForm.duration}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, duration: e.target.value }))}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Exercises</Label>
                  <TextArea
                    placeholder="List the exercises to be performed..."
                    value={workoutPlanForm.exercises}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, exercises: e.target.value }))}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Sets</Label>
                  <Input
                    type="text"
                    placeholder="e.g., 3 sets"
                    value={workoutPlanForm.sets}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, sets: e.target.value }))}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Reps</Label>
                  <Input
                    type="text"
                    placeholder="e.g., 8-12 reps"
                    value={workoutPlanForm.reps}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, reps: e.target.value }))}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Weight/Resistance</Label>
                  <Input
                    type="text"
                    placeholder="e.g., 20kg, bodyweight"
                    value={workoutPlanForm.weight}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, weight: e.target.value }))}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Estimated Calories Burned</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 300"
                    value={workoutPlanForm.caloriesBurned}
                    onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, caloriesBurned: e.target.value }))}
                  />
                </FormGroup>
              </>
            )}
            
            <FormGroup>
              <Label>Notes</Label>
              <TextArea
                placeholder="Any additional instructions or notes..."
                value={workoutPlanForm.notes}
                onChange={(e) => setWorkoutPlanForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </FormGroup>
            
            <ButtonGroup>
              <CancelButton onClick={() => setShowWorkoutPlanModal(false)}>
                <FaTimes /> Cancel
              </CancelButton>
              <SaveButton onClick={handleSaveWorkoutPlan} disabled={saving}>
                <FaSave /> {saving ? 'Saving...' : 'Save Workout Plan'}
              </SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ClientDetailsView;


